import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiList, FiX, FiChevronRight } = FiIcons;

const TableOfContents = ({ contentRef }) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [showMobileModal, setShowMobileModal] = useState(false);
  
  const location = useLocation();
  
  const desktopScrollContainerRef = useRef(null);
  const mobileScrollContainerRef = useRef(null);
  const activeItemRefs = useRef({});
  const observerRef = useRef(null);

  useEffect(() => {
    if (!contentRef?.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    setHeadings([]);
    setActiveId('');
    setExpandedSections(new Set());
    activeItemRefs.current = {};

    const timeoutId = setTimeout(() => {
      if (!contentRef?.current) return;

      const elements = contentRef.current.querySelectorAll('h2[id], h3[id]');
      
      if (elements.length === 0) {
        console.warn('TableOfContents: 未找到任何標題元素');
        return;
      }

      const headingData = Array.from(elements).map((element) => ({
        id: element.id,
        text: element.textContent,
        level: element.tagName.toLowerCase()
      }));

      setHeadings(headingData);

      const allH2Ids = headingData.filter(h => h.level === 'h2').map(h => h.id);
      setExpandedSections(new Set(allH2Ids));

      if (headingData.length > 0) {
        setActiveId(headingData[0].id);
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const visibleEntries = entries.filter(entry => entry.isIntersecting);
          
          if (visibleEntries.length > 0) {
            const topEntry = visibleEntries.reduce((top, entry) => {
              return entry.boundingClientRect.top < top.boundingClientRect.top ? entry : top;
            });
            setActiveId(topEntry.target.id);
          }
        },
        {
          rootMargin: '-100px 0px -66%',
          threshold: [0, 0.25, 0.5, 0.75, 1]
        }
      );

      elements.forEach((element) => {
        if (observerRef.current) {
          observerRef.current.observe(element);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [contentRef, location.pathname]);

  useEffect(() => {
    if (!activeId) return;

    const activeElement = activeItemRefs.current[activeId];
    if (!activeElement) return;

    const desktopContainer = desktopScrollContainerRef.current;
    if (desktopContainer) {
      scrollToActiveItem(desktopContainer, activeElement);
    }

    const mobileContainer = mobileScrollContainerRef.current;
    if (mobileContainer && showMobileModal) {
      scrollToActiveItem(mobileContainer, activeElement);
    }
  }, [activeId, showMobileModal]);

  const scrollToActiveItem = (container, activeElement) => {
    if (!container || !activeElement) return;

    const containerRect = container.getBoundingClientRect();
    const activeRect = activeElement.getBoundingClientRect();

    const relativeTop = activeRect.top - containerRect.top + container.scrollTop;
    
    const idealScrollTop = relativeTop - containerRect.height / 3;

    container.scrollTo({
      top: Math.max(0, idealScrollTop),
      behavior: 'smooth'
    });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setShowMobileModal(false);
    }
  };

  const toggleSection = (h2Id) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(h2Id)) {
        newSet.delete(h2Id);
      } else {
        newSet.add(h2Id);
      }
      return newSet;
    });
  };

  if (headings.length === 0) {
    return null;
  }

  const structuredHeadings = [];
  let currentH2 = null;

  headings.forEach((heading) => {
    if (heading.level === 'h2') {
      currentH2 = { ...heading, children: [] };
      structuredHeadings.push(currentH2);
    } else if (heading.level === 'h3' && currentH2) {
      currentH2.children.push(heading);
    }
  });

  const currentIndex = headings.findIndex(h => h.id === activeId);
  const progress = currentIndex >= 0 ? currentIndex + 1 : 1;
  const total = headings.length;
  const progressPercentage = total > 0 ? Math.round((progress / total) * 100) : 0;

  const renderTocContent = () => (
    <nav className="space-y-1">
      {structuredHeadings.map((h2) => {
        const isExpanded = expandedSections.has(h2.id);
        const isActive = activeId === h2.id;
        const hasActiveChild = h2.children.some(h3 => h3.id === activeId);

        return (
          <div key={h2.id}>
            <div className="flex items-start gap-1">
              {h2.children.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSection(h2.id);
                  }}
                  className="flex-shrink-0 p-1 mt-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded transition-colors"
                  aria-label={isExpanded ? '收起' : '展開'}
                >
                  <SafeIcon
                    icon={FiChevronRight}
                    className={`w-4 h-4 transition-transform ${
                      isExpanded ? 'rotate-90' : ''
                    } ${
                      isActive || hasActiveChild
                        ? 'text-orange-500'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  />
                </button>
              )}
              <button
                ref={(el) => {
                  if (el) activeItemRefs.current[h2.id] = el;
                }}
                onClick={() => scrollToSection(h2.id)}
                className={`
                  flex-1 text-left px-3 py-2 rounded-lg transition-all text-sm font-medium
                  ${
                    isActive
                      ? 'bg-orange-500/10 text-orange-500 font-semibold border-l-4 border-orange-500'
                      : hasActiveChild
                      ? 'text-orange-400 dark:text-orange-400'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }
                `}
              >
                <span className="line-clamp-2">{h2.text}</span>
              </button>
            </div>

            <AnimatePresence>
              {isExpanded && h2.children.length > 0 && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="ml-5 mt-1 space-y-1 border-l-2 border-slate-200 dark:border-slate-700 pl-3">
                    {h2.children.map((h3) => {
                      const isH3Active = activeId === h3.id;
                      return (
                        <button
                          key={h3.id}
                          ref={(el) => {
                            if (el) activeItemRefs.current[h3.id] = el;
                          }}
                          onClick={() => scrollToSection(h3.id)}
                          className={`
                            block w-full text-left px-3 py-1.5 rounded-lg transition-all text-sm
                            ${
                              isH3Active
                                ? 'bg-orange-500/10 text-orange-500 font-semibold border-l-4 border-orange-500'
                                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-white'
                            }
                          `}
                        >
                          <span className="line-clamp-2">{h3.text}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* 桌面版 */}
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="hidden lg:block bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border-2 border-slate-200 dark:border-slate-700 sticky top-24 max-h-[calc(100vh-200px)] overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <span>📚</span> 文章目錄
          </h3>
          <span className="text-xs font-semibold text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-1 rounded-full">
            {progressPercentage}%
          </span>
        </div>

        <div className="mb-4 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-orange-500 to-amber-500"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        <div className="absolute top-[88px] left-0 right-0 h-8 bg-gradient-to-b from-white/60 dark:from-slate-800/60 to-transparent pointer-events-none z-10" />

        <div 
          ref={desktopScrollContainerRef}
          className="overflow-y-auto max-h-[calc(100vh-300px)] pr-2 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent"
        >
          {renderTocContent()}
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/60 dark:from-slate-800/60 to-transparent pointer-events-none" />
      </motion.div>

      {/* 移動版 */}
      <div className="lg:hidden">
        {/* 🍬 修正 1: 浮動按鈕增加安全距離，避免與其他元素衝突 */}
        <motion.button
          key={location.pathname}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowMobileModal(true)}
          className="fixed bottom-24 right-4 z-40 bg-gradient-to-br from-orange-500 to-amber-500 text-white p-4 rounded-full shadow-2xl flex items-center justify-center"
          aria-label="打開文章目錄"
        >
          <SafeIcon icon={FiList} className="w-6 h-6" />
          
          <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="rgba(255,255,255,0.3)"
              strokeWidth="4"
            />
            <motion.circle
              cx="50"
              cy="50"
              r="46"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: progressPercentage / 100 }}
              transition={{ duration: 0.5 }}
              style={{
                strokeDasharray: "289.027",
                strokeDashoffset: 289.027 * (1 - progressPercentage / 100)
              }}
            />
          </svg>
          
          {/* 🍬 修正 2: 百分比標籤優化，確保可讀性 */}
          <span className="absolute -top-2 -right-2 bg-white text-orange-500 text-xs font-bold rounded-full min-w-[2rem] h-8 flex items-center justify-center shadow-lg border-2 border-orange-500 px-1">
            {progressPercentage}%
          </span>
        </motion.button>

        <AnimatePresence>
          {showMobileModal && (
            <>
              {/* 🍬 修正 3: 背景遮罩層級優化 */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowMobileModal(false)}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
              />

              {/* 🍬 修正 4: Modal 主體 - 關鍵修正：從 top-0 改為 top-16，避開導航欄 */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="fixed top-16 right-0 bottom-0 w-full sm:w-96 bg-white dark:bg-slate-900 z-[70] shadow-2xl overflow-hidden flex flex-col"
              >
                {/* 🍬 修正 5: Header 優化 - 移除多餘的 padding-top，因為已經有 top-16 的安全距離 */}
                <div className="flex items-center justify-between p-4 border-b-2 border-slate-200 dark:border-slate-700 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-slate-800 dark:to-slate-800">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <span>📚</span> 文章目錄
                    </h3>
                    <div className="flex items-center gap-3 mt-2">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {progress} / {total}
                      </p>
                      <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-orange-500 to-amber-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${progressPercentage}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <span className="text-sm font-bold text-orange-500">
                        {progressPercentage}%
                      </span>
                    </div>
                  </div>
                  
                  {/* 🍬 修正 6: 關閉按鈕優化，增加觸控區域 */}
                  <button
                    onClick={() => setShowMobileModal(false)}
                    className="ml-4 p-2.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors active:scale-95"
                    aria-label="關閉目錄"
                  >
                    <SafeIcon icon={FiX} className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                  </button>
                </div>

                {/* 🍬 修正 7: 內容區域 - 優化滾動體驗 */}
                <div 
                  ref={mobileScrollContainerRef}
                  className="flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent"
                >
                  {renderTocContent()}
                </div>

                {/* 🍬 修正 8: 底部漸層提示，增強 UX */}
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white dark:from-slate-900 to-transparent pointer-events-none" />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default TableOfContents;